import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Stack,
  Grid,
  Box,
  useTheme,
  IconButton,
  InputBase,
  Divider,
  Badge,
  Chip,
  Fade,
  Slide,
  Paper,
} from "@mui/material";
import {
  Close,
  FilterList,
  Search,
  CheckBoxOutlineBlank,
  CheckBox,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const FilterModal = ({
  open,
  onClose,
  onApply,
  filters,
  attendees,
  roleName,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState(filters);
  const [filterCategory, setFilterCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter options with counts
  const filterOptions = {
    role: {
      items: [...new Set(attendees.map((a) => a.role))],
      selected: localFilters.role,
      label: t("filterModal.role"),
      visible: roleName === "attendees",
    },
    company: {
      items: [...new Set(attendees.map((a) => a.company || a.company_name))],
      selected: localFilters.company || localFilters.company_name,
      label: t("filterModal.company"),
    },
    designation: {
      items: [...new Set(attendees.map((a) => a.designation || a.brands))],
      selected: localFilters.designation,
      label: t("filterModal.designation"),
      visible: roleName !== "products" || roleName !== "exhibitor",
    },
    country: {
      items: [...new Set(attendees.map((a) => a.country))],
      selected: localFilters.country,
      label: t("filterModal.country"),
    },
  };

  const handleFilterToggle = (category, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter((item) => item !== value)
        : [...prev[category], value],
    }));
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleClear = (category) => {
    setLocalFilters((prev) => ({ ...prev, [category]: [] }));
  };

  const handleClearAll = () => {
    setLocalFilters({ role: [], company: [], designation: [], country: [] });
    onApply({ role: [], company: [], designation: [], country: [] });
  };

  const filteredItems = filterCategory
    ? filterOptions[filterCategory]?.items?.filter((item) =>
        item?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      )
    : [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{ transitionDuration: 300 }}
    >
      <Fade in={open}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            p: 2,
          }}
        >
          <Paper
            sx={{
              width: "100%",
              maxWidth: 500,
              maxHeight: "90vh",
              bgcolor: "background.paper",
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: theme.shadows[10],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="h5"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <FilterList fontSize="small" />
                {t("filterModal.filter")}
              </Typography>
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </Box>

            {/* Content */}
            <Box
              sx={{
                p: 3,
                overflowY: "auto",
                maxHeight: "calc(90vh - 160px)",
              }}
            >
              {!filterCategory ? (
                <Stack spacing={2}>
                  {Object.entries(filterOptions)
                    .sort((a, b) => a[1].label.localeCompare(b[1].label))
                    .map(
                      ([key, option]) =>
                        option.visible !== false && (
                          <Button
                            key={key}
                            fullWidth
                            variant="containedGreen"
                            onClick={() => setFilterCategory(key)}
                            sx={{
                              justifyContent: "space-between",
                              py: 1.5,
                              borderRadius: 2,
                              borderColor: "divider",
                              bgcolor:
                                option.selected.length > 0
                                  ? theme.palette.action.selected
                                  : "background.paper",
                            }}
                          >
                            <Typography variant="body1" sx={{ color: "white" }}>
                              {option.label}
                            </Typography>
                            <Chip
                              label={option.selected.length}
                              size="small"
                              color="primary"
                              sx={{
                                ml: 1,
                                visibility:
                                  option.selected.length > 0
                                    ? "visible"
                                    : "hidden",
                              }}
                            />
                          </Button>
                        )
                    )}
                </Stack>
              ) : (
                <Slide
                  direction="left"
                  in={!!filterCategory}
                  mountOnEnter
                  unmountOnExit
                >
                  <div>
                    {/* Search Bar */}
                    <InputBase
                      fullWidth
                      placeholder={`${t("Search")} ${
                        filterOptions[filterCategory].label
                      }`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{
                        mb: 2,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: "background.default",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                      startAdornment={
                        <Search
                          fontSize="small"
                          sx={{ mr: 1, color: "text.secondary" }}
                        />
                      }
                    />

                    {/* Filter Items */}
                    <Stack spacing={1}>
                      {filteredItems
                        .slice() // Create a copy to avoid mutating original array
                        .sort((a, b) => a.localeCompare(b)) // Alphabetical sorting
                        .map((item) => (
                          <FormControlLabel
                            key={item}
                            control={
                              <Checkbox
                                checked={localFilters[filterCategory].includes(
                                  item
                                )}
                                onChange={() =>
                                  handleFilterToggle(filterCategory, item)
                                }
                                icon={<CheckBoxOutlineBlank />}
                                checkedIcon={<CheckBox />}
                                color="primary"
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{ textTransform: "capitalize" }}
                              >
                                {item}
                              </Typography>
                            }
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          />
                        ))}
                    </Stack>
                  </div>
                </Slide>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: "background.paper",
              }}
            >
              {filterCategory ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => {
                        setFilterCategory(null);
                        setSearchQuery("");
                      }}
                      startIcon={<Close fontSize="small" />}
                    >
                      {t("Back")}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => handleClear(filterCategory)}
                    >
                      {t("clear")}
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleApply}
                      disabled={Object.values(localFilters).every(
                        (arr) => arr.length === 0
                      )}
                    >
                      {t("ApplyFilters")}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="text"
                      color="error"
                      onClick={handleClearAll}
                    >
                      {t("ClearAll")}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Modal>
  );
};

FilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    role: PropTypes.array.isRequired,
    company: PropTypes.array.isRequired,
    designation: PropTypes.array.isRequired,
    country: PropTypes.array.isRequired,
  }).isRequired,
  attendees: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.string.isRequired,
      company: PropTypes.string.isRequired,
      designation: PropTypes.string.isRequired,
      country: PropTypes.string.isRequired,
    })
  ).isRequired,
  roleName: PropTypes.string.isRequired,
};

export default FilterModal;
